import { useState, useRef, useId } from 'react';
import { ChatCircle, Bug, Lightbulb, ChatDots, PaperPlaneTilt, CircleNotch, EyeSlash, Image as ImageIcon, X } from '@phosphor-icons/react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import Tippy from '@tippyjs/react';
import { animateFill } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/dist/backdrop.css';
import 'tippy.js/animations/shift-away.css';

const CATEGORIES = [
  { value: 'bug', label: 'Bug', icon: Bug, description: 'Algo não funciona' },
  { value: 'suggestion', label: 'Sugestão', icon: Lightbulb, description: 'Ideia de melhoria' },
  { value: 'other', label: 'Outro', icon: ChatDots, description: 'Dúvida ou comentário' },
] as const;

type CategoryValue = (typeof CATEGORIES)[number]['value'];

export function FeedbackModal({
  controlled_open,
  on_controlled_change,
  hidden_trigger,
}: {
  controlled_open?: boolean;
  on_controlled_change?: (v: boolean) => void;
  hidden_trigger?: boolean;
} = {}) {
  const { user } = useAuth();
  const instance_id = useId();
  const [internal_open, set_internal_open] = useState(false);

  const open = controlled_open ?? internal_open;
  const set_open = (v: boolean) => {
    set_internal_open(v);
    on_controlled_change?.(v);
  };
  const [category, set_category] = useState<CategoryValue>('suggestion');
  const selected_index = CATEGORIES.findIndex((cat) => cat.value === category);
  const [message, set_message] = useState('');
  const [anonymous, set_anonymous] = useState(false);
  const [sending, set_sending] = useState(false);
  const [image_file, set_image_file] = useState<File | null>(null);
  const [image_preview, set_image_preview] = useState<string | null>(null);
  const file_input_ref = useRef<HTMLInputElement>(null);

  const handle_image_select = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo: 5MB');
      return;
    }
    set_image_file(file);
    const reader = new FileReader();
    reader.onload = () => set_image_preview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const remove_image = () => {
    set_image_file(null);
    set_image_preview(null);
    if (file_input_ref.current) file_input_ref.current.value = '';
  };

  const handle_submit = async () => {
    if (!user || !message.trim()) return;
    set_sending(true);

    let uploaded_url: string | null = null;

    // Upload imagem se existir
    if (image_file) {
      const ext = image_file.name.split('.').pop() ?? 'png';
      const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
      const { error: upload_err } = await supabase.storage
        .from('feedback-images')
        .upload(path, image_file, { contentType: image_file.type });
      if (upload_err) {
        toast.error('Erro ao enviar imagem. Tente novamente.');
        set_sending(false);
        return;
      }
      const { data: url_data } = supabase.storage.from('feedback-images').getPublicUrl(path);
      uploaded_url = url_data.publicUrl;
    }

    const ticket_id = crypto.randomUUID();
    const { error } = await supabase.from('feedback_tickets').insert({
      id: ticket_id,
      user_id: user.id,
      category,
      message: message.trim(),
      image_url: uploaded_url,
    });

    if (error) {
      set_sending(false);
      toast.error('Não foi possível enviar. Tente novamente.');
      return;
    }

    // Notificar por email (fire-and-forget)
    void supabase.functions.invoke('send-transactional-email', {
      body: {
        templateName: 'feedback-notification',
        idempotencyKey: `feedback-${ticket_id}`,
        templateData: {
          category,
          message: message.trim(),
          user_email: anonymous ? 'Anônimo' : user.email,
        },
      },
    });

    set_sending(false);
    toast.success('Obrigado pelo feedback! Nossa equipe vai analisar.');
    set_message('');
    set_category('suggestion');
    set_image_file(null);
    set_image_preview(null);
    set_open(false);
  };

  return (
    <Dialog open={open} onOpenChange={set_open}>
      {!hidden_trigger && (
        <Tippy content="Enviar sugestão" placement="bottom" animateFill plugins={[animateFill]} arrow={false}>
          <span className="inline-flex">
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground"
                aria-label="Enviar sugestão"
              >
                <ChatCircle className="h-4 w-4" />
              </Button>
            </DialogTrigger>
          </span>
        </Tippy>
      )}
      <DialogContent className="sm:max-w-md rounded-2xl border-none p-0 overflow-hidden gap-0">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
        >
          {/* Cabeçalho */}
          <div className="px-6 pt-6 pb-4">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold tracking-tight">Enviar feedback</DialogTitle>
              <p className="text-sm text-muted-foreground/80 mt-1 leading-relaxed">
                Encontrou um problema ou tem uma sugestão? Nossa equipe vai analisar.
              </p>
            </DialogHeader>
          </div>

          <div className="px-6 pb-6 space-y-4">
            {/* Categorias */}
            <div className="relative grid grid-cols-3 gap-2">
              <motion.span
                aria-hidden="true"
                className="absolute inset-y-0 z-0 rounded-xl bg-accent"
                initial={false}
                animate={{
                  width: 'calc((100% - 1rem) / 3)',
                  left: selected_index === 0
                    ? '0%'
                    : selected_index === 1
                      ? 'calc((100% - 1rem) / 3 + 0.5rem)'
                      : 'calc((((100% - 1rem) / 3) * 2) + 1rem)',
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 34 }}
                data-instance={instance_id}
              />
              {CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const is_active = category === cat.value;
                return (
                  <button
                    key={cat.value}
                    onClick={() => set_category(cat.value)}
                    className={cn(
                      'relative flex flex-col items-center gap-1.5 rounded-xl px-3 py-3 text-sm font-medium transition-all duration-200',
                      is_active
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                    )}
                  >
                    <Icon className="relative z-10 h-5 w-5" weight={is_active ? 'fill' : 'regular'} />
                    <span className="relative z-10 text-xs">{cat.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Campo de texto */}
            <Textarea
              value={message}
              onChange={(e) => set_message(e.target.value)}
              placeholder="Descreva o problema ou sua sugestão..."
              className="min-h-[120px] resize-none rounded-xl border-none bg-accent/40 focus-visible:ring-1 focus-visible:ring-primary/30 placeholder:text-muted-foreground/60"
              maxLength={2000}
            />

            {/* Preview da imagem */}
            <AnimatePresence>
              {image_preview && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="relative overflow-hidden"
                >
                  <div className="relative rounded-xl overflow-hidden bg-accent/30">
                    <img src={image_preview} alt="Preview" className="w-full max-h-40 object-cover" />
                    <button
                      onClick={remove_image}
                      className="absolute top-2 right-2 h-6 w-6 rounded-full bg-background/80 backdrop-blur flex items-center justify-center hover:bg-background transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Rodapé de ações */}
            <div className="flex items-center gap-2">
              {/* Anexar imagem */}
              <input
                ref={file_input_ref}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handle_image_select}
              />
              <Tippy content="Anexar imagem" placement="top" animateFill plugins={[animateFill]} arrow={false}>
                <button
                  type="button"
                  onClick={() => file_input_ref.current?.click()}
                  className={cn(
                    'flex items-center justify-center h-8 w-8 rounded-lg transition-colors',
                    image_file
                      ? 'bg-primary/10 text-primary'
                      : 'bg-accent/50 text-muted-foreground hover:text-foreground'
                  )}
                >
                  <ImageIcon className="h-4 w-4" weight={image_file ? 'fill' : 'regular'} />
                </button>
              </Tippy>

              {/* Anônimo toggle */}
              <button
                type="button"
                onClick={() => set_anonymous((v) => !v)}
                className={cn(
                  'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
                  anonymous
                    ? 'bg-primary/10 text-primary'
                    : 'bg-accent/50 text-muted-foreground hover:text-foreground'
                )}
              >
                <EyeSlash className="h-3.5 w-3.5" weight={anonymous ? 'fill' : 'regular'} />
                {anonymous ? 'Anônimo' : 'Anônimo'}
              </button>

              <span className="flex-1" />

              <span className="text-[11px] text-muted-foreground/60 tabular-nums mr-1">
                {message.length.toLocaleString('pt-BR')}/2.000
              </span>

              <Button
                onClick={() => void handle_submit()}
                disabled={sending || !message.trim()}
                size="sm"
                className="rounded-xl gap-1.5 h-8"
              >
                {sending ? (
                  <><CircleNotch className="h-3.5 w-3.5 animate-spin" />Enviando</>
                ) : (
                  <><PaperPlaneTilt className="h-3.5 w-3.5" />Enviar</>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
